/**
 * Command Palette Component
 * Palette de commandes pour accès rapide aux fonctionnalités
 */

let paletteInstance = null;
let commands = []; // Toutes les commandes globales
let activeFilterCommands = []; // Commandes du filtre actif (après filtrage par catégorie)
let filteredCommands = []; // Commandes affichées (après recherche)
let selectedIndex = 0;
let previousActiveElement = null;
let uniqueId = 0;
let favorites = []; // IDs des commandes favorites
let history = []; // IDs des commandes récemment utilisées (historique)
let historyFeatureEnabled = false; // Fonctionnalité historique activée par le dev (false par défaut)
let historyEnabled = false; // Active/désactive l'historique par l'utilisateur (false par défaut)
let historyMaxSize = 10; // Nombre max de commandes dans l'historique
let compactModeFeatureEnabled = false; // Fonctionnalité mode compact activée par le dev (false par défaut)
let compactModeEnabled = false; // Active/désactive le mode compact par l'utilisateur (false par défaut)
let tagSearchEnabled = false; // Fonctionnalité recherche par tags activée par le dev (false par défaut)
let activeFilter = null; // Filtre actif (null = recherche globale dans tous les filtres)
let filters = []; // Configuration des filtres personnalisés
let searchIndex = null; // Cache du search-index.json
let searchIndexPath = null; // Chemin par défaut (null = doit être défini)
let onNavigate = null; // Callback de navigation personnalisé pour SPA
let activeSubFilter = "all"; // Sous-filtre actif (pour filtrer par folder dans le JSON)
let labels = {
  favorites: "Favoris",
  recents: "Récents",
  defaultCategory: "Général",
}; // Labels personnalisables

// Charger les favoris depuis localStorage
function loadFavorites() {
  try {
    const stored = localStorage.getItem("command-palette-favorites");
    favorites = stored ? JSON.parse(stored) : [];
  } catch (e) {
    favorites = [];
  }
}

// Sauvegarder les favoris dans localStorage
function saveFavorites() {
  try {
    localStorage.setItem(
      "command-palette-favorites",
      JSON.stringify(favorites)
    );
  } catch (e) {
    console.warn("Impossible de sauvegarder les favoris");
  }
}

// Ajouter une commande aux favoris
function addToFavorites(commandId) {
  if (!favorites.includes(commandId)) {
    favorites.push(commandId);
    saveFavorites();
  }
}

// Retirer une commande des favoris
function removeFromFavorites(commandId) {
  favorites = favorites.filter((id) => id !== commandId);
  saveFavorites();
}

// Vérifier si une commande est favorite
function isFavorite(commandId) {
  return favorites.includes(commandId);
}

// Basculer le statut favori d'une commande
function toggleFavorite(commandId) {
  if (isFavorite(commandId)) {
    removeFromFavorites(commandId);
  } else {
    addToFavorites(commandId);
  }
}

// Charger l'historique depuis localStorage
function loadHistory() {
  try {
    const stored = localStorage.getItem("command-palette-history");
    history = stored ? JSON.parse(stored) : [];
  } catch (e) {
    history = [];
  }
}

// Sauvegarder l'historique dans localStorage
function saveHistory() {
  try {
    localStorage.setItem("command-palette-history", JSON.stringify(history));
  } catch (e) {
    console.warn("Impossible de sauvegarder l'historique");
  }
}

// Ajouter une commande à l'historique
function addToHistory(commandId) {
  if (!historyEnabled) return;

  // Ignorer si la commande est déjà en position 1 (utilisation consécutive)
  if (history[0] === commandId) return;

  // Retirer la commande si elle existe ailleurs dans l'historique
  history = history.filter((id) => id !== commandId);

  // Ajouter en tête de liste (plus récent)
  history.unshift(commandId);

  // Limiter la taille de l'historique
  if (history.length > historyMaxSize) {
    history = history.slice(0, historyMaxSize);
  }

  saveHistory();
}

// Vérifier si une commande est dans l'historique
function isInHistory(commandId) {
  return history.includes(commandId);
}

// Effacer tout l'historique
function clearHistory() {
  history = [];
  saveHistory();
}

// Charger le mode compact depuis localStorage
function loadCompactMode() {
  try {
    const stored = localStorage.getItem("command-palette-compact-mode");
    compactModeEnabled = stored ? JSON.parse(stored) : false;
  } catch (e) {
    compactModeEnabled = false;
  }
}

// Sauvegarder le mode compact dans localStorage
function saveCompactMode() {
  try {
    localStorage.setItem(
      "command-palette-compact-mode",
      JSON.stringify(compactModeEnabled)
    );
  } catch (e) {
    console.warn("Impossible de sauvegarder le mode compact");
  }
}

// Appliquer le mode compact sur la palette
function applyCompactMode() {
  if (!paletteInstance) return;

  // Récupérer la section des filtres principaux
  const filtersSection = paletteInstance.overlay.querySelector(
    ".command-palette__filters-section .filter"
  );

  if (compactModeEnabled) {
    paletteInstance.palette.classList.add("command-palette--compact");
    // Ajouter filter--sm aux filtres principaux
    if (filtersSection) {
      filtersSection.classList.add("filter--sm");
    }
  } else {
    paletteInstance.palette.classList.remove("command-palette--compact");
    // Retirer filter--sm des filtres principaux
    if (filtersSection) {
      filtersSection.classList.remove("filter--sm");
    }
  }
}

/**
 * Initialise la Command Palette
 * @param {HTMLElement} container - Le conteneur parent
 */
export function initCommandPalette(container = document) {
  // Charger les favoris depuis localStorage
  loadFavorites();

  // Charger le mode compact depuis localStorage
  loadCompactMode();

  // Pré-charger le search-index.json en arrière-plan si disponible
  if (searchIndexPath) {
    loadSearchIndex().catch((err) => {
      console.warn("Pré-chargement du search-index échoué:", err);
    });
  }

  // Écouter les raccourcis clavier globaux
  document.addEventListener("keydown", handleGlobalKeydown);
}

/**
 * Gère les raccourcis clavier globaux
 * @param {KeyboardEvent} e - L'événement clavier
 */
function handleGlobalKeydown(e) {
  // Cmd+K (Mac) ou Ctrl+K (Windows/Linux)
  if ((e.metaKey || e.ctrlKey) && e.key === "k") {
    e.preventDefault();
    toggleCommandPalette();
  }

  // Échap pour fermer
  if (e.key === "Escape" && paletteInstance) {
    closeCommandPalette();
  }
}

/**
 * Bascule l'affichage de la palette
 */
export function toggleCommandPalette() {
  if (paletteInstance) {
    closeCommandPalette();
  } else {
    openCommandPalette();
  }
}

/**
 * Ouvre la Command Palette
 * @param {Array} customCommands - Liste optionnelle de commandes personnalisées
 */
export async function openCommandPalette(customCommands = null) {
  if (paletteInstance) return;

  // Sauvegarder l'élément actuellement focusé
  previousActiveElement = document.activeElement;

  // ⚡ NOUVEAU : Réinitialiser le filtre actif à null (recherche globale)
  activeFilter = null;

  // Utiliser les commandes personnalisées passées en paramètre,
  // sinon les commandes globales définies avec setCommands(),
  // sinon les commandes par défaut (vide)
  commands = customCommands || commands || getDefaultCommands();

  // ⚡ NOUVEAU : Charger toutes les commandes (recherche globale)
  await updateActiveFilterCommands();

  selectedIndex = 0;

  // Générer un ID unique pour cette instance
  const instanceId = `command-palette-${++uniqueId}`;
  const resultsId = `${instanceId}-results`;

  // Créer l'overlay
  const overlay = document.createElement("div");
  overlay.className = "command-palette-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Palette de commandes");

  // Créer la palette
  const palette = document.createElement("div");
  palette.className = "command-palette";

  // Header avec input
  const header = createHeader(resultsId);
  palette.appendChild(header);

  // Section des filtres (si configurés)
  const filtersSection = createFiltersSection();
  if (filtersSection) {
    palette.appendChild(filtersSection);
  }

  // Section des sous-filtres (pour filtrer par folder dans le JSON)
  const subFiltersSection = await createSubFiltersSection();
  palette.appendChild(subFiltersSection);

  // Résultats
  const results = document.createElement("div");
  results.className = "command-palette__results";
  results.setAttribute("role", "listbox");
  results.id = resultsId;
  palette.appendChild(results);

  // Live region pour annoncer les résultats
  const liveRegion = document.createElement("div");
  liveRegion.className = "sr-only";
  liveRegion.setAttribute("role", "status");
  liveRegion.setAttribute("aria-live", "polite");
  liveRegion.setAttribute("aria-atomic", "true");
  palette.appendChild(liveRegion);

  // Footer
  const footer = createFooter();
  palette.appendChild(footer);

  overlay.appendChild(palette);
  document.body.appendChild(overlay);

  paletteInstance = {
    overlay,
    palette,
    input: header.querySelector(".command-palette__input"),
    results,
    liveRegion,
    instanceId,
    countBadge: header.querySelector(".command-palette__count"),
  };

  // Afficher avec animation
  requestAnimationFrame(() => {
    overlay.classList.add("is-open");
  });

  // Focus sur l'input
  paletteInstance.input.focus();

  // Initialiser l'UI des filtres (affiche/masque les sous-filtres selon le filtre actif)
  await updateFilterUI();

  // Appliquer le mode compact si activé
  applyCompactMode();

  // Afficher les résultats initiaux
  renderResults();

  // Écouter les clics sur l'overlay pour fermer
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      closeCommandPalette();
    }
  });

  // Écouter l'input
  paletteInstance.input.addEventListener("input", handleSearch);
  paletteInstance.input.addEventListener("keydown", handleKeydown);

  // Focus trap
  overlay.addEventListener("keydown", handleFocusTrap);
}

/**
 * Ferme la Command Palette
 */
export function closeCommandPalette() {
  if (!paletteInstance) return;

  const { overlay } = paletteInstance;

  // Animation de fermeture
  overlay.classList.remove("is-open");

  setTimeout(() => {
    overlay.remove();
    paletteInstance = null;

    // Restaurer le focus sur l'élément précédent
    if (previousActiveElement && previousActiveElement.focus) {
      previousActiveElement.focus();
    }
    previousActiveElement = null;
  }, 200);
}

/**
 * Crée le header avec l'input de recherche
 * @param {string} resultsId - L'ID du conteneur de résultats
 * @returns {HTMLElement}
 */
function createHeader(resultsId) {
  const header = document.createElement("div");
  header.className = "command-palette__header";

  const icon = document.createElement("div");
  icon.className = "command-palette__icon";
  icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.3-4.3"/>
  </svg>`;

  const input = document.createElement("input");
  input.type = "text";
  input.className = "command-palette__input";
  input.placeholder = "Rechercher...";
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-expanded", "true");
  input.setAttribute("aria-controls", resultsId);
  input.setAttribute("aria-haspopup", "listbox");

  const countBadge = document.createElement("div");
  countBadge.className = "command-palette__count badge badge-sm";
  countBadge.textContent = "0";

  header.appendChild(icon);
  header.appendChild(input);
  header.appendChild(countBadge);

  return header;
}

/**
 * Met à jour les commandes du filtre actif selon les catégories spécifiées
 * Appelée lors du changement de filtre pour recalculer les commandes affichées
 */
async function updateActiveFilterCommands() {
  const currentCommands = commands || [];
  let allCommands = [];

  // ⚡ NOUVEAU : Si aucun filtre actif (null), rechercher dans TOUS les filtres
  if (activeFilter === null) {
    // Collecter toutes les commandes manuelles
    allCommands = [...currentCommands];

    // Ajouter aussi tous les résultats JSON de tous les filtres qui en ont
    const jsonFilters = filters.filter((f) => f.searchInJson);
    for (const filter of jsonFilters) {
      try {
        const index = await loadSearchIndex();
        const jsonConfig = filter.jsonConfig || {};
        const jsonCommands = index.map((entry) =>
          searchEntryToCommand(entry, jsonConfig)
        );
        allCommands = allCommands.concat(jsonCommands);
      } catch (e) {
        console.warn(`Impossible de charger le JSON pour ${filter.label}:`, e);
      }
    }
  } else {
    // Un filtre spécifique est actif
    const currentFilter = filters.find((f) => f.id === activeFilter);

    if (currentFilter?.searchInJson) {
      // Si le filtre recherche dans le JSON, charger tout le JSON
      try {
        const index = await loadSearchIndex();
        const jsonConfig = currentFilter.jsonConfig || {};
        allCommands = index.map((entry) =>
          searchEntryToCommand(entry, jsonConfig)
        );
      } catch (e) {
        console.warn("Impossible de charger le JSON:", e);
      }
    } else {
      // Filtrer les commandes classiques selon les catégories spécifiées
      if (currentFilter?.categories && currentFilter.categories.length > 0) {
        // Si des catégories sont spécifiées, ne garder que celles-là
        allCommands = currentCommands.filter((cmd) =>
          currentFilter.categories.includes(cmd.category)
        );
      } else {
        // Sinon, afficher toutes les commandes
        allCommands = currentCommands;
      }
    }
  }

  // Stocker les commandes du filtre actif pour la recherche
  activeFilterCommands = allCommands;
  filteredCommands = allCommands;
}

/**
 * Crée la section des filtres de recherche
 * @returns {HTMLElement}
 */
function createFiltersSection() {
  // Ne pas créer de section si aucun filtre n'est configuré
  if (!filters || filters.length === 0) {
    return null;
  }

  const section = document.createElement("div");
  section.className = "command-palette__filters-section";

  const filterGroup = document.createElement("div");
  filterGroup.className = "filter";
  filterGroup.setAttribute("role", "radiogroup");
  filterGroup.setAttribute("aria-label", "Filtrer les résultats");

  // ⚡ NOUVEAU : Bouton "Réinitialiser" qui n'apparaît que quand un filtre est actif
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "filter__reset";
  resetBtn.textContent = "×";
  resetBtn.hidden = activeFilter === null; // Utiliser hidden au lieu de style.display
  resetBtn.setAttribute("aria-label", "Réinitialiser les filtres");

  resetBtn.addEventListener("click", async () => {
    // Réinitialiser : aucun filtre actif
    activeFilter = null;

    // Décocher tous les filtres radio
    const allInputs = filterGroup.querySelectorAll('input[name="search-filter"]');
    allInputs.forEach(input => input.checked = false);

    // Masquer le bouton reset
    resetBtn.hidden = true;

    // Re-charger toutes les commandes
    await updateActiveFilterCommands();
    await updateFilterUI();

    // Vider l'input et afficher tous les résultats
    paletteInstance.input.value = "";
    await handleSearch({ target: paletteInstance.input });
  });

  filterGroup.appendChild(resetBtn);

  // Ajouter les filtres personnalisés (aucun coché par défaut)
  filters.forEach((filter) => {
    const input = document.createElement("input");
    input.type = "radio";
    input.id = `filter-${filter.id}`;
    input.name = "search-filter";
    input.value = filter.id;
    input.checked = false; // Aucun filtre coché par défaut

    const label = document.createElement("label");
    label.htmlFor = `filter-${filter.id}`;
    label.className = "filter__option";
    label.textContent = filter.label;

    input.addEventListener("change", async () => {
      activeFilter = filter.id;

      // Afficher le bouton reset
      resetBtn.hidden = false;

      await updateActiveFilterCommands();
      await updateFilterUI();
      await handleSearch({ target: paletteInstance.input });
    });

    filterGroup.appendChild(input);
    filterGroup.appendChild(label);
  });

  section.appendChild(filterGroup);
  return section;
}

/**
 * Crée la section de sous-filtres pour filtrer par folder dans le JSON
 * @returns {HTMLElement}
 */
async function createSubFiltersSection() {
  const section = document.createElement("div");
  section.className = "command-palette__subfilters-section";
  section.style.display = "none"; // Caché par défaut

  const filterGroup = document.createElement("div");
  filterGroup.className = "filter filter--sm";

  // Option "Tout" pour les sous-filtres (pas besoin de bouton reset, "Tout" fait la même chose)
  const allInput = document.createElement("input");
  allInput.type = "radio";
  allInput.id = "subfilter-all";
  allInput.name = "search-subfilter";
  allInput.value = "all";
  allInput.checked = true;

  const allLabel = document.createElement("label");
  allLabel.htmlFor = "subfilter-all";
  allLabel.className = "filter__option";
  allLabel.textContent = "Tout";

  allInput.addEventListener("change", () => {
    activeSubFilter = "all";
    updateSubFilterUI();
    handleSearch({ target: paletteInstance.input });
  });

  filterGroup.appendChild(allInput);
  filterGroup.appendChild(allLabel);

  section.appendChild(filterGroup);
  return section;
}

/**
 * Remplit les sous-filtres dynamiquement basés sur le JSON
 */
async function populateSubFilters() {
  if (!paletteInstance) return;

  const subFiltersSection = paletteInstance.overlay.querySelector(
    ".command-palette__subfilters-section"
  );
  if (!subFiltersSection) return;

  const filterGroup = subFiltersSection.querySelector(".filter");

  // Récupérer la configuration du filtre actif
  const currentFilter = filters.find((f) => f.id === activeFilter);
  if (!currentFilter || !currentFilter.jsonConfig) return;

  const jsonConfig = currentFilter.jsonConfig;
  const subFilterField = jsonConfig.subFilterField || "folder";

  // Charger l'index et extraire les valeurs uniques du champ configuré
  const index = await loadSearchIndex();
  const subFilterValues = extractSubFilters(index, subFilterField);

  // Supprimer les anciens sous-filtres (garder uniquement "Tout")
  const existingOptions = filterGroup.querySelectorAll(
    'input[name="search-subfilter"]:not(#subfilter-all)'
  );
  existingOptions.forEach((input) => {
    const label = filterGroup.querySelector(`label[for="${input.id}"]`);
    if (label) label.remove();
    input.remove();
  });

  // Ajouter les nouveaux sous-filtres
  subFilterValues.forEach((value) => {
    const input = document.createElement("input");
    input.type = "radio";
    input.id = `subfilter-${value}`;
    input.name = "search-subfilter";
    input.value = value;

    const label = document.createElement("label");
    label.htmlFor = `subfilter-${value}`;
    label.className = "filter__option";
    // Capitaliser et remplacer les tirets par des espaces
    label.textContent = value
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

    input.addEventListener("change", () => {
      activeSubFilter = value;
      updateSubFilterUI();
      handleSearch({ target: paletteInstance.input });
    });

    filterGroup.appendChild(input);
    filterGroup.appendChild(label);
  });
}

/**
 * Met à jour l'UI des sous-filtres
 * Note: Fonction conservée pour compatibilité, mais ne fait plus rien
 * (pas de bouton reset à gérer, le filtre "Tout" suffit)
 */
function updateSubFilterUI() {
  // Plus rien à faire ici, fonction conservée pour éviter les erreurs
  // si elle est appelée ailleurs dans le code
}

/**
 * Met à jour l'UI des filtres (placeholder et sous-filtres)
 */
async function updateFilterUI() {
  if (!paletteInstance) return;

  // Afficher/masquer la section de sous-filtres
  const currentFilter = activeFilter ? filters.find((f) => f.id === activeFilter) : null;
  const subFiltersSection = paletteInstance.overlay.querySelector(
    ".command-palette__subfilters-section"
  );

  if (subFiltersSection) {
    if (currentFilter && currentFilter.searchInJson) {
      // Afficher les sous-filtres et les populer
      subFiltersSection.style.display = "block";
      await populateSubFilters();
      // Réinitialiser le sous-filtre actif
      activeSubFilter = "all";
      const allSubFilterInput =
        subFiltersSection.querySelector("#subfilter-all");
      if (allSubFilterInput) allSubFilterInput.checked = true;
      updateSubFilterUI();
    } else {
      // Masquer les sous-filtres
      subFiltersSection.style.display = "none";
      activeSubFilter = "all";
    }
  }

  // Mettre à jour le placeholder de l'input selon le filtre actif
  if (activeFilter === null) {
    // ⚡ NOUVEAU : Aucun filtre actif = recherche globale
    paletteInstance.input.placeholder = "Rechercher partout...";
  } else if (currentFilter && currentFilter.searchInJson) {
    paletteInstance.input.placeholder = `Rechercher dans ${
      currentFilter.label ? currentFilter.label.toLowerCase() : "le contenu"
    }...`;
  } else if (currentFilter) {
    paletteInstance.input.placeholder = `Rechercher dans ${
      currentFilter.label ? currentFilter.label.toLowerCase() : "les commandes"
    }...`;
  } else {
    paletteInstance.input.placeholder = "Rechercher...";
  }
}

/**
 * Crée le footer avec les hints clavier et le bouton de paramètres
 * @returns {HTMLElement}
 */
function createFooter() {
  const footer = document.createElement("div");
  footer.className = "command-palette__footer";

  const hints = document.createElement("div");
  hints.className = "command-palette__footer-hints";
  hints.innerHTML = `
    <div class="command-palette__footer-hint">
      <kbd class="kbd kbd--xs">↑</kbd><kbd class="kbd kbd--xs">↓</kbd>
      <span>Naviguer</span>
    </div>
    <div class="command-palette__footer-hint">
      <kbd class="kbd kbd--xs">Enter</kbd>
      <span>Sélectionner</span>
    </div>
    <div class="command-palette__footer-hint">
      <kbd class="kbd kbd--xs">ESC</kbd>
      <span>Fermer</span>
    </div>
  `;

  footer.appendChild(hints);

  // Wrapper du popover de paramètres
  const popoverWrapper = document.createElement("div");
  popoverWrapper.className =
    "popover popover--top-end popover--sm popover--fit";

  // Bouton de configuration pour ouvrir le popover des paramètres
  const settingsBtn = document.createElement("button");
  settingsBtn.type = "button";
  settingsBtn.className = "btn btn-icon btn-sm btn-secondary btn-circle";
  settingsBtn.setAttribute("aria-label", "Paramètres");
  settingsBtn.tabIndex = 0;
  settingsBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  `;

  // Contenu du popover
  const popoverContent = document.createElement("div");
  popoverContent.className = "popover__content";
  popoverContent.tabIndex = -1;

  // Vérifier si au moins une fonctionnalité est activée
  const hasAnyFeature = historyFeatureEnabled || compactModeFeatureEnabled;

  if (hasAnyFeature) {
    let switchesHTML = "";

    // Switch historique
    if (historyFeatureEnabled) {
      switchesHTML += `
        <div class="switch-field switch-field--sm w-full mb-xs">
          <div class="switch-field__wrapper justify-between">
            <label for="history-toggle-switch" class="switch-field__label">Activer l'historique</label>
            <input type="checkbox" id="history-toggle-switch" class="switch-field__input" ${
              historyEnabled ? "checked" : ""
            }>
            <label for="history-toggle-switch" class="switch-field__track">
              <span class="switch-field__thumb"></span>
            </label>
          </div>
        </div>
      `;
    }

    // Switch mode compact
    if (compactModeFeatureEnabled) {
      switchesHTML += `
        <div class="switch-field switch-field--sm w-full">
          <div class="switch-field__wrapper justify-between">
            <label for="compact-mode-toggle-switch" class="switch-field__label">Vue compacte</label>
            <input type="checkbox" id="compact-mode-toggle-switch" class="switch-field__input" ${
              compactModeEnabled ? "checked" : ""
            }>
            <label for="compact-mode-toggle-switch" class="switch-field__track">
              <span class="switch-field__thumb"></span>
            </label>
          </div>
        </div>
      `;
    }

    popoverContent.innerHTML = switchesHTML;

    // Gérer le changement du switch historique
    if (historyFeatureEnabled) {
      const historyToggle = popoverContent.querySelector(
        "#history-toggle-switch"
      );
      historyToggle.addEventListener("change", (e) => {
        historyEnabled = e.target.checked;

        // Sauvegarder dans localStorage
        try {
          localStorage.setItem(
            "command-palette-history-enabled",
            JSON.stringify(historyEnabled)
          );
        } catch (err) {
          console.warn(
            "Impossible de sauvegarder le paramètre historique:",
            err
          );
        }

        // Re-render pour afficher/masquer la section Récents
        if (paletteInstance) {
          renderResults(paletteInstance.input.value.toLowerCase().trim());
        }
      });
    }

    // Gérer le changement du switch mode compact
    if (compactModeFeatureEnabled) {
      const compactToggle = popoverContent.querySelector(
        "#compact-mode-toggle-switch"
      );
      compactToggle.addEventListener("change", (e) => {
        compactModeEnabled = e.target.checked;
        saveCompactMode();
        applyCompactMode();
      });
    }
  } else {
    // Aucun paramètre activé, afficher un message
    popoverContent.innerHTML = `
      <p class="body-sm opacity-6"">Aucun paramètre</p>
    `;
  }

  // Assembler le popover
  popoverWrapper.appendChild(settingsBtn);
  popoverWrapper.appendChild(popoverContent);

  footer.appendChild(popoverWrapper);

  return footer;
}

/**
 * Charge le search-index.json de manière asynchrone
 * @returns {Promise<Array>}
 */
async function loadSearchIndex() {
  if (searchIndex) {
    return searchIndex;
  }

  try {
    const response = await fetch(searchIndexPath);
    if (!response.ok) {
      console.warn("Impossible de charger le search-index.json");
      return [];
    }
    searchIndex = await response.json();
    return searchIndex;
  } catch (error) {
    console.warn("Erreur lors du chargement du search-index.json:", error);
    return [];
  }
}

/**
 * Extrait les sous-filtres uniques du search-index basés sur un champ configurable
 * @param {Array} index - Le search index JSON
 * @param {string} fieldName - Nom du champ à utiliser pour extraire les valeurs
 * @returns {Array} Tableau des valeurs uniques
 */
function extractSubFilters(index, fieldName = "folder") {
  if (!index || index.length === 0) return [];

  const values = new Set();
  index.forEach((entry) => {
    if (entry[fieldName]) {
      values.add(entry[fieldName]);
    }
  });

  return Array.from(values).sort();
}

/**
 * Convertit une entrée du search-index en commande
 * @param {Object} entry - Entrée du search-index
 * @param {Object} jsonConfig - Configuration du mapping JSON
 * @param {string} query - Requête de recherche (pour le contexte)
 * @returns {Object}
 */
function searchEntryToCommand(entry, jsonConfig = {}, query = "") {
  const {
    subFilterField = "folder",
    categoryField = "folder",
    iconMapping = {},
  } = jsonConfig;

  // Icône par défaut
  let icon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>';

  // Déterminer l'icône depuis le mapping personnalisé
  const subFilterValue = entry[subFilterField];
  if (subFilterValue && iconMapping[subFilterValue]) {
    icon = iconMapping[subFilterValue];
  }
  // Sinon, on garde l'icône par défaut définie plus haut (icône de fichier générique)

  // Construire le titre et la description
  let title = entry.title;
  let description = "";

  // Construire le breadcrumb de contexte pour la description
  if (entry.section) {
    // Si on a une section parente, construire le chemin
    const breadcrumbParts = [];

    // Ajouter la catégorie capitalisée (ex: "Styles")
    if (entry.type) {
      breadcrumbParts.push(entry.type);
    }

    // Ajouter la section parente (ex: "Couleurs")
    breadcrumbParts.push(entry.section);

    // Si on a une subsection, l'ajouter aussi
    if (entry.subsection) {
      breadcrumbParts.push(entry.subsection);
    }

    description = breadcrumbParts.join(" › ");
  } else {
    // Page principale : juste la catégorie
    description = entry.type || labels.defaultCategory;
  }

  // Déterminer la catégorie depuis le champ configuré
  const category = entry[categoryField] || labels.defaultCategory;

  return {
    id: `search-${entry.url}${entry.anchor ? `-${entry.anchor}` : ""}`,
    title: title,
    description: description,
    category: category, // Utilise le champ configuré
    icon: icon,
    anchor: entry.anchor, // Stocker l'ancre pour l'utiliser plus tard
    action: () => {
      // Si un callback de navigation personnalisé est défini, l'utiliser
      // Sinon, utiliser window.location.href par défaut
      if (onNavigate && typeof onNavigate === "function") {
        onNavigate(entry.url, entry.anchor);
      } else {
        window.location.href = entry.url;
      }
    },
  };
}

/**
 * Recherche dans le search-index.json
 * @param {string} query - Requête de recherche
 * @param {Array} index - Index de recherche
 * @param {Object} jsonConfig - Configuration du mapping JSON
 * @returns {Array}
 */
function searchInIndex(query, index, jsonConfig = {}) {
  if (!query || !index || index.length === 0) {
    return [];
  }

  const lowerQuery = query.toLowerCase();
  const results = [];

  index.forEach((entry) => {
    // Rechercher dans le titre
    const titleMatch = fuzzyMatch(lowerQuery, entry.title.toLowerCase());

    // Rechercher dans la section
    const sectionMatch = entry.section
      ? fuzzyMatch(lowerQuery, entry.section.toLowerCase())
      : false;

    // Rechercher dans la sous-section
    const subsectionMatch = entry.subsection
      ? fuzzyMatch(lowerQuery, entry.subsection.toLowerCase())
      : false;

    // Rechercher dans les tags si la fonctionnalité est activée
    let tagsMatch = false;
    if (tagSearchEnabled && entry.tags && Array.isArray(entry.tags)) {
      tagsMatch = entry.tags.some(tag =>
        fuzzyMatch(lowerQuery, tag.toLowerCase())
      );
    }

    if (titleMatch || sectionMatch || subsectionMatch || tagsMatch) {
      results.push(searchEntryToCommand(entry, jsonConfig));
    }
  });

  return results;
}

/**
 * Gère la recherche
 * @param {Event} e
 */
async function handleSearch(e) {
  const query = e.target.value.toLowerCase().trim();

  // Vérifier si le filtre actif nécessite une recherche dans le JSON
  const currentFilter = filters.find((f) => f.id === activeFilter);
  const shouldSearchInJson = currentFilter && currentFilter.searchInJson;

  let results = [];

  // Si on doit rechercher dans le JSON
  if (shouldSearchInJson) {
    const index = await loadSearchIndex();
    const jsonConfig = currentFilter.jsonConfig || {};
    const subFilterField = jsonConfig.subFilterField || "folder";

    // Appliquer le sous-filtre si actif
    let indexToSearch = index;
    if (activeSubFilter !== "all") {
      indexToSearch = index.filter(
        (entry) => entry[subFilterField] === activeSubFilter
      );
    }

    if (query) {
      // Recherche avec query : filtrer les résultats
      results = searchInIndex(query, indexToSearch, jsonConfig);
    } else {
      // Pas de query : afficher TOUT le JSON (ou filtré par sous-filtre)
      results = indexToSearch.map((entry) =>
        searchEntryToCommand(entry, jsonConfig)
      );
    }
  } else {
    // Mode sans searchInJson : recherche dans les commandes du filtre actif
    if (!query) {
      // Sans query : afficher toutes les commandes du filtre actif
      results = activeFilterCommands;
    } else {
      // Avec query : recherche floue dans les commandes du filtre actif
      results = activeFilterCommands.filter((cmd) => {
        const titleMatch = fuzzyMatch(query, cmd.title.toLowerCase());
        const descMatch = cmd.description
          ? fuzzyMatch(query, cmd.description.toLowerCase())
          : false;

        // Rechercher dans les tags si la fonctionnalité est activée
        let tagsMatch = false;
        if (tagSearchEnabled && cmd.tags && Array.isArray(cmd.tags)) {
          tagsMatch = cmd.tags.some(tag =>
            fuzzyMatch(query, tag.toLowerCase())
          );
        }

        return titleMatch || descMatch || tagsMatch;
      });
    }
  }

  filteredCommands = results;
  selectedIndex = 0;
  renderResults(query);
}

/**
 * Recherche floue basique
 * @param {string} query - La requête
 * @param {string} text - Le texte à comparer
 * @returns {boolean}
 */
function fuzzyMatch(query, text) {
  let queryIndex = 0;
  let textIndex = 0;

  while (queryIndex < query.length && textIndex < text.length) {
    if (query[queryIndex] === text[textIndex]) {
      queryIndex++;
    }
    textIndex++;
  }

  return queryIndex === query.length;
}

/**
 * Highlight les caractères correspondants
 * @param {string} text - Le texte
 * @param {string} query - La requête
 * @returns {string}
 */
function highlightMatches(text, query) {
  if (!query) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  let result = "";
  let queryIndex = 0;
  let textIndex = 0;

  while (textIndex < text.length) {
    if (
      queryIndex < lowerQuery.length &&
      lowerText[textIndex] === lowerQuery[queryIndex]
    ) {
      result += `<mark>${text[textIndex]}</mark>`;
      queryIndex++;
    } else {
      result += text[textIndex];
    }
    textIndex++;
  }

  return result;
}

/**
 * Génère une couleur de badge cohérente basée sur la catégorie
 * @param {string} category - Le nom de la catégorie
 * @returns {string} - La classe CSS du badge
 */
function getBadgeColorForCategory(category) {
  // Hash simple de la chaîne pour obtenir un index
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Palette de couleurs disponibles
  const colors = ["badge--information", "badge--success", "badge--warning"];

  // Retourner une couleur basée sur le hash (toujours la même pour la même catégorie)
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

/**
 * Affiche les résultats
 * @param {string} query - La requête de recherche
 */
function renderResults(query = "") {
  const { results, liveRegion, instanceId, countBadge } = paletteInstance;
  results.innerHTML = "";

  // Mettre à jour le badge de comptage
  countBadge.textContent = filteredCommands.length.toString();

  if (filteredCommands.length === 0) {
    // Aucun résultat trouvé
    results.innerHTML = `
      <div class="command-palette__empty">
        <div class="command-palette__empty-icon">
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ><path d="m13.5 8.5-5 5"/><path d="m8.5 8.5 5 5"/><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
        </div>
        <div class="command-palette__empty-title">Aucun résultat</div>
        <div class="command-palette__empty-description">
          Essayez une autre recherche ...
        </div>
      </div>
    `;
    liveRegion.textContent = "Aucun résultat trouvé";
    return;
  }

  // 🔹 Collecter les commandes RÉCENTES (historique)
  const allRecentCommands = [];

  // Helper pour trouver une commande par son ID (dans commandes manuelles ou JSON)
  // Retourne { cmd, filterLabel }
  const findCommandById = (commandId) => {
    // Chercher dans les commandes manuelles
    const currentCommands = commands || [];
    const manualCmd = currentCommands.find((c) => c.id === commandId);
    if (manualCmd) {
      return {
        cmd: manualCmd,
        filterLabel: findMostSpecificFilter(manualCmd),
      };
    }

    // Chercher dans les commandes JSON
    let result = null;
    filters.forEach((filter) => {
      if (filter.searchInJson && searchIndex && !result) {
        const jsonConfig = filter.jsonConfig || {};
        const entry = searchIndex.find((e) => {
          const cmd = searchEntryToCommand(e, jsonConfig);
          return cmd.id === commandId;
        });
        if (entry) {
          result = {
            cmd: searchEntryToCommand(entry, jsonConfig),
            filterLabel: filter.label, // ← Utiliser le label du filtre JSON
          };
        }
      }
    });
    return result;
  };

  // 🔹 Collecter TOUS les favoris de TOUS les filtres
  const allFavoriteCommands = [];

  // Helper pour trouver le filtre le plus spécifique pour une commande
  const findMostSpecificFilter = (cmd) => {
    // Chercher d'abord un filtre avec des catégories spécifiques qui matchent
    const specificFilter = filters.find(
      (f) =>
        !f.searchInJson &&
        f.categories &&
        f.categories.length > 0 &&
        f.categories.includes(cmd.category)
    );

    if (specificFilter) return specificFilter.label;

    // Sinon, utiliser la catégorie de la commande elle-même
    return cmd.category;
  };

  // Parcourir toutes les commandes favorites
  const currentCommands = commands || [];
  currentCommands.forEach((cmd) => {
    if (isFavorite(cmd.id)) {
      allFavoriteCommands.push({
        ...cmd,
        filterLabel: findMostSpecificFilter(cmd),
      });
    }
  });

  // Parcourir les favoris dans les filtres JSON
  filters.forEach((filter) => {
    if (filter.searchInJson && searchIndex) {
      const jsonConfig = filter.jsonConfig || {};
      searchIndex.forEach((entry) => {
        const cmd = searchEntryToCommand(entry, jsonConfig);
        if (
          isFavorite(cmd.id) &&
          !allFavoriteCommands.find((c) => c.id === cmd.id)
        ) {
          allFavoriteCommands.push({
            ...cmd,
            filterLabel: filter.label,
          });
        }
      });
    }
  });

  // Parcourir l'historique pour collecter les commandes récentes
  if (historyEnabled && history.length > 0) {
    history.forEach((commandId) => {
      const result = findCommandById(commandId);
      if (result && !allRecentCommands.find((c) => c.id === result.cmd.id)) {
        allRecentCommands.push({
          ...result.cmd,
          filterLabel: result.filterLabel, // ← Utiliser le filterLabel retourné
        });
      }
    });
  }

  // Les commandes du filtre actif (en EXCLUANT les favoris cette fois)
  const otherCommands = filteredCommands.filter((cmd) => !isFavorite(cmd.id));

  // Grouper les commandes autres par catégorie
  const grouped = {};
  otherCommands.forEach((cmd) => {
    if (!grouped[cmd.category]) {
      grouped[cmd.category] = [];
    }
    grouped[cmd.category].push(cmd);
  });

  // On maintient un compteur global pour l'index
  let globalIndex = 0;

  // Calculer le nombre total de groupes pour savoir où mettre les dividers
  const totalGroups =
    (historyEnabled ? 1 : 0) +
    (allFavoriteCommands.length > 0 ? 1 : 0) +
    Object.keys(grouped).length;
  let groupCount = 0;

  // Afficher la section récents si l'historique est activé
  if (historyEnabled) {
    const recentGroup = document.createElement("div");
    recentGroup.className = "command-palette__group";

    const recentTitle = document.createElement("div");
    recentTitle.className = "command-palette__group-title";
    recentTitle.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon-info"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
      <span>${labels.recents}</span>
      ${
        allRecentCommands.length > 0
          ? `
        <button type="button" class="has-tooltip tooltip-md p-0 btn btn-icon btn-sm ml-auto" aria-label="Vider l'historique" title="Vider l'historique" id="clear-history-btn" data-tooltip="Vider l'historique" data-placement="left">
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon-danger"><path d="m16 22-1-4"/><path d="M19 14a1 1 0 0 0 1-1v-1a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v1a1 1 0 0 0 1 1"/><path d="M19 14H5l-1.973 6.767A1 1 0 0 0 4 22h16a1 1 0 0 0 .973-1.233z"/><path d="m8 22 1-4"/></svg>
        </button>
      `
          : ""
      }
    `;
    recentGroup.appendChild(recentTitle);

    // Ajouter l'event listener pour le bouton de suppression
    if (allRecentCommands.length > 0) {
      const clearBtn = recentTitle.querySelector("#clear-history-btn");
      if (clearBtn) {
        clearBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          clearHistory();
          // Re-render pour masquer la section récents
          if (paletteInstance) {
            renderResults(paletteInstance.input.value.toLowerCase().trim());
          }
        });
      }
    }

    // Si l'historique est vide, afficher un message
    if (allRecentCommands.length === 0) {
      const emptyMessage = document.createElement("div");
      emptyMessage.className = "command-palette__empty-message";
      emptyMessage.innerHTML = `
        <p class="body-x-sm">Historique vide ...</p>
      `;
      recentGroup.appendChild(emptyMessage);
    }

    allRecentCommands.forEach((cmd) => {
      const currentIndex = globalIndex;
      const itemId = `${instanceId}-item-${currentIndex}`;
      const item = createCommandItem(
        cmd,
        query,
        currentIndex === selectedIndex,
        itemId,
        true // 🔹 Afficher le badge de catégorie pour les récents
      );
      item.dataset.index = currentIndex;
      item.addEventListener("click", (e) => {
        // Ne pas exécuter si on clique sur l'étoile
        if (!e.target.closest(".command-palette__favorite-btn")) {
          executeCommand(cmd);
        }
      });
      item.addEventListener("mouseenter", () => {
        selectedIndex = currentIndex;
        updateSelection();
      });
      recentGroup.appendChild(item);
      globalIndex++;
    });

    results.appendChild(recentGroup);
    groupCount++;

    // Ajouter divider si ce n'est pas le dernier groupe et qu'on a plus d'un groupe
    if (totalGroups > 1 && groupCount < totalGroups) {
      const divider = document.createElement("div");
      divider.className = "command-palette__divider";
      results.appendChild(divider);
    }
  }

  // Afficher ensuite les favoris si il y en a
  if (allFavoriteCommands.length > 0) {
    const favGroup = document.createElement("div");
    favGroup.className = "command-palette__group";

    const favTitle = document.createElement("div");
    favTitle.className = "command-palette__group-title";
    favTitle.innerHTML = `
<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke-width="0"
      stroke-linecap="round" stroke-linejoin="round" class="icon-danger"><path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/></svg>
      ${labels.favorites}
    `;
    favGroup.appendChild(favTitle);

    allFavoriteCommands.forEach((cmd) => {
      const currentIndex = globalIndex;
      const itemId = `${instanceId}-item-${currentIndex}`;
      const item = createCommandItem(
        cmd,
        query,
        currentIndex === selectedIndex,
        itemId,
        true // 🔹 Afficher le badge de catégorie pour les favoris
      );
      item.dataset.index = currentIndex;
      item.addEventListener("click", (e) => {
        // Ne pas exécuter si on clique sur l'étoile
        if (!e.target.closest(".command-palette__favorite-btn")) {
          executeCommand(cmd);
        }
      });
      item.addEventListener("mouseenter", () => {
        selectedIndex = currentIndex;
        updateSelection();
      });
      favGroup.appendChild(item);
      globalIndex++;
    });

    results.appendChild(favGroup);
    groupCount++;

    // Ajouter divider si ce n'est pas le dernier groupe et qu'on a plus d'un groupe
    if (totalGroups > 1 && groupCount < totalGroups) {
      const divider = document.createElement("div");
      divider.className = "command-palette__divider";
      results.appendChild(divider);
    }
  }

  // Afficher les groupes par catégorie
  const groupEntries = Object.entries(grouped);
  groupEntries.forEach(([category, cmds], index) => {
    const group = document.createElement("div");
    group.className = "command-palette__group";

    const title = document.createElement("div");
    title.className = "command-palette__group-title";
    title.textContent = category;
    group.appendChild(title);

    cmds.forEach((cmd) => {
      const currentIndex = globalIndex;
      const itemId = `${instanceId}-item-${currentIndex}`;
      const item = createCommandItem(
        cmd,
        query,
        currentIndex === selectedIndex,
        itemId
      );
      item.dataset.index = currentIndex; // Stocker l'index global
      item.addEventListener("click", (e) => {
        // Ne pas exécuter si on clique sur l'étoile
        if (!e.target.closest(".command-palette__favorite-btn")) {
          executeCommand(cmd);
        }
      });
      item.addEventListener("mouseenter", () => {
        selectedIndex = currentIndex;
        updateSelection();
      });
      group.appendChild(item);
      globalIndex++; // Incrémenter pour le prochain item
    });

    results.appendChild(group);
    groupCount++;

    // Ajouter divider si ce n'est pas le dernier groupe et qu'on a plus d'un groupe
    if (totalGroups > 1 && groupCount < totalGroups) {
      const divider = document.createElement("div");
      divider.className = "command-palette__divider";
      results.appendChild(divider);
    }
  });

  // Annoncer le nombre de résultats
  const count = filteredCommands.length;
  liveRegion.textContent = `${count} résultat${count > 1 ? "s" : ""} trouvé${
    count > 1 ? "s" : ""
  }`;

  // Mettre à jour aria-activedescendant
  updateSelection();
}

/**
 * Crée un item de commande
 * @param {Object} command - La commande
 * @param {string} query - La requête de recherche
 * @param {boolean} isSelected - Si l'item est sélectionné
 * @param {string} itemId - L'ID unique de l'item
 * @param {boolean} showCategoryBadge - Afficher un badge avec la catégorie (pour les favoris)
 * @returns {HTMLElement}
 */
function createCommandItem(
  command,
  query,
  isSelected,
  itemId,
  showCategoryBadge = false
) {
  const item = document.createElement("div");
  item.className = "command-palette__item";
  item.id = itemId;
  if (isSelected) {
    item.classList.add("is-selected");
  }
  item.setAttribute("role", "option");
  item.setAttribute("aria-selected", isSelected);

  // Icône
  if (command.icon) {
    const icon = document.createElement("div");
    icon.className = "command-palette__item-icon";
    icon.innerHTML = command.icon;
    item.appendChild(icon);
  }

  // Contenu
  const content = document.createElement("div");
  content.className = "command-palette__item-content";

  const title = document.createElement("div");
  title.className = "command-palette__item-title";
  title.innerHTML = highlightMatches(command.title, query);

  content.appendChild(title);

  if (command.description) {
    const description = document.createElement("div");
    description.className = "command-palette__item-description";
    description.textContent = command.description;
    content.appendChild(description);
  }

  item.appendChild(content);

  // Bouton favori (étoile)
  const favoriteBtn = document.createElement("button");
  favoriteBtn.className = "command-palette__favorite-btn";
  favoriteBtn.type = "button";
  favoriteBtn.setAttribute(
    "aria-label",
    isFavorite(command.id) ? "Retirer des favoris" : "Ajouter aux favoris"
  );
  favoriteBtn.innerHTML = isFavorite(command.id)
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke-width="0"
      stroke-linecap="round" stroke-linejoin="round"><path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="1.5" stroke="currentColor"
      stroke-linecap="round" stroke-linejoin="round"><path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/></svg>`;

  favoriteBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleFavorite(command.id);
    // Re-render pour mettre à jour l'affichage
    const query = paletteInstance.input.value;
    handleSearch({ target: { value: query } });
  });

  item.appendChild(favoriteBtn);

  // Raccourci
  if (command.shortcut) {
    const shortcut = document.createElement("div");
    shortcut.className = "command-palette__item-shortcut";
    const keys = command.shortcut.split("+");
    keys.forEach((key) => {
      const kbd = document.createElement("kbd");
      kbd.className = "kbd kbd--xs"; // Utiliser le composant kbd existant
      kbd.textContent = key;
      shortcut.appendChild(kbd);
    });
    item.appendChild(shortcut);
  }

  // Badge de catégorie (uniquement pour les favoris) - Toujours à la fin
  if (showCategoryBadge && command.filterLabel) {
    const badge = document.createElement("span");
    const badgeColor = getBadgeColorForCategory(command.filterLabel);
    badge.className = `badge badge-xs ${badgeColor}`;
    badge.textContent = command.filterLabel;
    item.appendChild(badge);
  }

  return item;
}

/**
 * Gère la navigation clavier
 * @param {KeyboardEvent} e
 */
function handleKeydown(e) {
  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, filteredCommands.length - 1);
      updateSelection();
      break;

    case "ArrowUp":
      e.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, 0);
      updateSelection();
      break;

    case "Enter":
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        executeCommand(filteredCommands[selectedIndex]);
      }
      break;

    case "Escape":
      e.preventDefault();
      closeCommandPalette();
      break;
  }
}

/**
 * Gère le focus trap pour garder le focus dans la modale
 * @param {KeyboardEvent} e
 */
function handleFocusTrap(e) {
  if (e.key !== "Tab") return;

  const { palette } = paletteInstance;
  const focusableElements = palette.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const focusableArray = Array.from(focusableElements);
  const firstFocusable = focusableArray[0];
  const lastFocusable = focusableArray[focusableArray.length - 1];

  // Si Shift+Tab sur le premier élément, aller au dernier
  if (e.shiftKey && document.activeElement === firstFocusable) {
    e.preventDefault();
    lastFocusable.focus();
  }
  // Si Tab sur le dernier élément, aller au premier
  else if (!e.shiftKey && document.activeElement === lastFocusable) {
    e.preventDefault();
    firstFocusable.focus();
  }
}

/**
 * Met à jour la sélection visuelle
 */
function updateSelection() {
  const { input, results } = paletteInstance;
  const items = results.querySelectorAll(".command-palette__item");
  let activeItemId = null;

  items.forEach((item) => {
    const itemIndex = parseInt(item.dataset.index, 10);
    if (itemIndex === selectedIndex) {
      item.classList.add("is-selected");
      item.setAttribute("aria-selected", "true");
      item.scrollIntoView({ block: "nearest", behavior: "smooth" });
      activeItemId = item.id;
    } else {
      item.classList.remove("is-selected");
      item.setAttribute("aria-selected", "false");
    }
  });

  // Mettre à jour aria-activedescendant
  if (activeItemId) {
    input.setAttribute("aria-activedescendant", activeItemId);
  } else {
    input.removeAttribute("aria-activedescendant");
  }
}

/**
 * Exécute une commande
 * @param {Object} command - La commande à exécuter
 */
function executeCommand(command) {
  if (command.action) {
    command.action();
  }

  // Ajouter la commande à l'historique
  addToHistory(command.id);

  // Fermer la palette uniquement si keepOpen n'est pas défini ou est false
  if (!command.keepOpen) {
    closeCommandPalette();
  }
}

/**
 * Commandes par défaut
 * Par défaut, retourne un tableau vide.
 * Les projets doivent définir leurs propres commandes avec window.CommandPalette.setCommands()
 *
 * Exemple d'utilisation dans votre projet :
 * window.CommandPalette.setCommands([
 *   {
 *     id: "mon-action",
 *     title: "Mon action",
 *     description: "Description de l'action",
 *     category: "Actions",
 *     icon: "<svg>...</svg>",
 *     shortcut: "Ctrl+Shift+A",
 *     action: () => { ... }
 *   }
 * ]);
 *
 * @returns {Array}
 */
function getDefaultCommands() {
  return [];
}

/**
 * Définir des commandes personnalisées
 * @param {Array} customCommands - Liste de commandes
 */
export function setCommands(customCommands) {
  commands = customCommands;
}

/**
 * Définir des filtres personnalisés
 * @param {Array} customFilters - Liste de filtres
 *
 * Exemple :
 * window.CommandPalette.setFilters([
 *   {
 *     id: 'commandes',
 *     label: 'Commandes'
 *   },
 *   {
 *     id: 'documentation',
 *     label: 'Documentation',
 *     searchInJson: true,  // Active la recherche dans le JSON
 *     jsonConfig: {
 *       subFilterField: 'page',  // Champ pour générer les sous-filtres
 *       categoryField: 'type',   // Champ pour grouper les résultats (comme les catégories de commandes)
 *       iconMapping: {           // Mapping des icônes par valeur de sous-filtre
 *         'composants': '<svg>...</svg>',
 *         'utilitaires': '<svg>...</svg>',
 *         'guidelines': '<svg>...</svg>'
 *       }
 *     }
 *   }
 * ]);
 */
export function setFilters(customFilters) {
  filters = customFilters;
}

/**
 * Définir le chemin du fichier search-index.json
 * @param {string} path - Chemin vers le fichier JSON
 *
 * Exemple :
 * window.CommandPalette.setSearchIndexPath('/generated/search-index.json');
 */
export function setSearchIndexPath(path) {
  searchIndexPath = path;
  searchIndex = null; // Réinitialiser le cache
}

/**
 * Définir un callback de navigation personnalisé pour les résultats de recherche JSON
 * Utile pour les SPA (Single Page Applications) qui gèrent leur propre routing
 * @param {Function} callback - Fonction appelée avec l'URL en paramètre
 *
 * Exemple pour une SPA :
 * window.CommandPalette.setNavigationHandler((url) => {
 *   // Votre logique de navigation SPA (sans recharger la page)
 *   history.pushState(null, '', url);
 *   loadPageContent(url);
 * });
 */
export function setNavigationHandler(callback) {
  onNavigate = callback;
}

/**
 * Personnaliser les labels utilisés dans l'interface
 * @param {Object} customLabels - Objet contenant les labels personnalisés
 * @param {string} [customLabels.favorites] - Label pour la section des favoris (par défaut: "Favoris")
 * @param {string} [customLabels.defaultCategory] - Label par défaut pour les catégories non définies (par défaut: "Général")
 *
 * Exemple :
 * window.CommandPalette.setLabels({
 *   favorites: "My Favorites",
 *   defaultCategory: "Other"
 * });
 */
export function setLabels(customLabels) {
  labels = { ...labels, ...customLabels };
}

// Export pour usage global
if (typeof window !== "undefined") {
  window.CommandPalette = {
    init: initCommandPalette,
    open: openCommandPalette,
    close: closeCommandPalette,
    toggle: toggleCommandPalette,
    setCommands,
    setFilters,
    setSearchIndexPath,
    setNavigationHandler,
    setLabels,
    // Gestion des favoris
    getFavorites: () => [...favorites],
    clearFavorites: () => {
      favorites = [];
      saveFavorites();
    },
    // Gestion de l'historique
    enableHistory: () => {
      historyFeatureEnabled = true;
      // Charger l'état utilisateur depuis localStorage
      try {
        const stored = localStorage.getItem("command-palette-history-enabled");
        if (stored !== null) {
          historyEnabled = JSON.parse(stored);
        } else {
          // Par défaut, activé si la fonctionnalité est activée
          historyEnabled = true;
        }
      } catch (e) {
        historyEnabled = true;
      }
      loadHistory();
    },
    getHistory: () => [...history],
    clearHistory: () => {
      clearHistory();
      if (paletteInstance) {
        renderResults(paletteInstance.input.value.toLowerCase().trim());
      }
    },
    isHistoryEnabled: () => historyEnabled,
    setHistoryEnabled: (enabled) => {
      historyEnabled = enabled;
      try {
        localStorage.setItem(
          "command-palette-history-enabled",
          JSON.stringify(enabled)
        );
      } catch (e) {
        console.warn("Impossible de sauvegarder la préférence d'historique");
      }
      if (paletteInstance) {
        renderResults(paletteInstance.input.value.toLowerCase().trim());
      }
    },
    // Gestion du mode compact
    enableCompactMode: () => {
      compactModeFeatureEnabled = true;
      // Charger l'état utilisateur depuis localStorage
      try {
        const stored = localStorage.getItem("command-palette-compact-mode");
        if (stored !== null) {
          compactModeEnabled = JSON.parse(stored);
        } else {
          // Par défaut, désactivé
          compactModeEnabled = false;
        }
      } catch (e) {
        compactModeEnabled = false;
      }
    },
    isCompactModeEnabled: () => compactModeEnabled,
    setCompactMode: (enabled) => {
      compactModeEnabled = enabled;
      saveCompactMode();
      applyCompactMode();
    },
    // Gestion de la recherche par tags
    enableTagSearch: () => {
      tagSearchEnabled = true;
    },
    isTagSearchEnabled: () => tagSearchEnabled,
  };
}
